import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import { formatRelative } from '@/utils/format';
import type { AuditEvent } from '@/types';
import { Card } from '@/components/ui/Card';
import { Icon } from '@/components/icons';
import styles from './AdminAuditPage.module.css';

const CATEGORY_LABELS: Record<string, string> = {
  PROFILE: 'admin.audit.profileChanges',
  CAMPUS: 'admin.audit.campusChanges',
  USER: 'admin.audit.userInvitations',
  ROLE: 'admin.audit.roleAssignments',
  ACADEMIC_YEAR: 'admin.audit.academicYearChanges',
  BRANDING: 'admin.audit.brandingChanges',
  ANNOUNCEMENT: 'admin.audit.announcementChanges',
  CONFIGURATION: 'admin.audit.configurationChanges',
  IMPORT: 'admin.audit.importActivity',
  SUPPORT: 'admin.audit.supportActivity',
};

const FILTERABLE_CATEGORIES = Object.keys(CATEGORY_LABELS);

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

export function AdminAuditPage() {
  const { t, i18n } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  // Applied filters live in the URL so a refresh or shared link reproduces
  // the same view; typing is local state until Apply commits it.
  const [searchParams, setSearchParams] = useSearchParams();
  const applied = {
    actor: searchParams.get('actor') ?? '',
    category: searchParams.get('category') ?? '',
  };
  const [actorFilter, setActorFilter] = useState(applied.actor);
  const [categoryFilter, setCategoryFilter] = useState(applied.category);
  const applyFilters = () => {
    setSearchParams(
      (params) => {
        if (actorFilter) params.set('actor', actorFilter);
        else params.delete('actor');
        if (categoryFilter) params.set('category', categoryFilter);
        else params.delete('category');
        return params;
      },
      { replace: true },
    );
  };

  const { state, reload } = useAsyncData<AuditEvent[]>(
    `admin-audit-${institutionId}-${applied.actor}-${applied.category}`,
    () =>
      api.admin.audit.list(institutionId, {
        actor: applied.actor || undefined,
        category: applied.category || undefined,
      }),
    !!institutionId,
  );

  if (state.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('admin.audit.loading')}</div>
      </div>
    );
  }

  if (state.status === 'error') {
    const error = state.error;
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.audit.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t(error.isMissingEndpoint ? 'platform.failure.missing.body' : 'platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  const events = state.data;

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h1>{t('admin.audit.title')}</h1>
        <p className="page-subtitle">{t('admin.audit.subtitle')}</p>
      </div>

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="audit-actor">{t('admin.audit.filterActor')}</label>
          <input
            id="audit-actor"
            className={styles.searchInput}
            type="text"
            placeholder={t('admin.audit.search')}
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="audit-category">{t('admin.audit.filterAction')}</label>
          <select
            id="audit-category"
            className={styles.selectInput}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">—</option>
            {FILTERABLE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {t(CATEGORY_LABELS[cat] ?? '', cat)}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className={styles.applyBtn}
          onClick={applyFilters}
        >
          {t('common.apply')}
        </button>
      </div>

      <Card>
        {events.length === 0 ? (
          <p className={`${styles.emptyStateBody} ${styles.tableEmpty}`}>
            {t('admin.audit.noResults')}
          </p>
        ) : (
          <div className="table-scroll">
            <table className={styles.table}>
            <thead className={styles.tableHead}>
              <tr>
                <th>{t('admin.audit.timestamp')}</th>
                <th>{t('admin.audit.action')}</th>
                <th>{t('admin.audit.actor')}</th>
                <th>{t('admin.audit.context')}</th>
                <th>{t('admin.audit.details')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} className={styles.tableRow}>
                  <td
                    className={`${styles.tableCell} ${styles.timestampCell}`}
                    title={formatTimestamp(event.timestamp)}
                  >
                    {formatRelative(event.timestamp, i18n.language)}
                  </td>
                  <td className={`${styles.tableCell} ${styles.actionLabel}`}>{event.action}</td>
                  <td className={`${styles.tableCell} ${styles.actorLabel}`}>{event.actor}</td>
                  <td className={styles.tableCell}>
                    <span className={styles.categoryChip}>
                      {t(CATEGORY_LABELS[event.category] ?? '', event.category)}
                    </span>
                  </td>
                  <td className={`${styles.tableCell} ${styles.detailsText}`}>
                    {event.details ||
                      (event.beforeValue && event.afterValue
                        ? `${event.beforeValue} → ${event.afterValue}`
                        : '—')}
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
